import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatTimecode, timecodeAt, timecodeSpec } from "@infoflyt/shared/timecode";
import { erPickup } from "@infoflyt/shared/synk";
import { spec as specFor, type Oekt } from "../state/session";
import * as su from "../state/su";
import * as synk from "../state/synk";
import { useLevende } from "../state/levende";

/**
 * Reporterens synkflate.
 *
 * REPORTEREN SYNKER, OG SITTER OFTE MED PC. Det er en egen stilling med
 * synkene som hovedansvar, og den er noe annet enn kontoret, redaktøren og
 * A-foto: dette er ikke administrativt arbeid, det er opptak. Derfor ligger
 * flata under området «Opptak». Avgjort 24.10.2026.
 *
 * ========  SAMME FLYT SOM I APPEN: VELG, SÅ SYNK  ========
 *
 * Første utgave hadde alle deltakerne som kort ved siden av hverandre, som
 * Brief og Spørsmål. Det var feil form, og appen hadde alt svaret: man
 * VELGER den man skal synke, og så starter synken. Man sitter med én person,
 * og da skal skjermen vise én person. Omgjort 24.10.2026.
 *
 * Velgeren beholder rutenettet, fordi det er riktig DER: før man har valgt
 * er spørsmålet hvem som mangler hva, og det leses i ett blikk. Appen har et
 * hjul på samme sted — det er en fingerbevegelse, og den hører ikke på en PC.
 *
 * ========  SAMTALEN KOMMER FØRST  ========
 *
 * Mange reportere gjør synken som en SAMTALE. Da er det ikke alltid mulig å
 * huke av mens man snakker — men noen ganger er det det, og begge må virke.
 * Avhukingen er derfor mulig HELE TIDEN og påkrevd ALDRI:
 *
 * * «Start synk» setter tidskoden i loggen. Ett trykk, fordi det er alt man
 *   rekker før man setter seg ned.
 * * Haker kan settes underveis eller etterpå. Ingenting låses.
 * * «Ferdig med synk» SPØR om alt er huket av — det er ikke en sperre.
 * * Svarene skrives i ettertid, under hvert spørsmål, og kan rettes.
 *
 * ========  LISTA ER LOGGEN  ========
 *
 * Start og slutt blir markører med tidskode i reporterens egen logg, og
 * hvert avhuket spørsmål et svar koblet til startposten. Haken er ikke en
 * egen tilstand — et spørsmål er stilt hvis det finnes et svar og
 * startposten lever. Se migrasjon 20261011140000.
 *
 * ET STILT SPØRSMÅL BLIR STÅENDE I LISTA, i motsetning til i appen, der det
 * forsvinner. Her er det med vilje: svaret skrives under spørsmålet, oftest
 * i ettertid, og et spørsmål som er borte har ingen linje å skrive på. Det
 * er dempet og haket, så det forveksles ikke med et ustilt.
 */

/** Tidskoden for én post, regnet av dagens anker. Som i LoggScreen. */
function tidskode(anker: synk.Anker, p: synk.Synkpost): string {
  const rate = anker.framerate as Parameters<typeof timecodeSpec>[0];
  const spec = timecodeSpec(rate, anker.dropFrame);
  const t = timecodeAt(
    {
      tcFrames: anker.tcFrames,
      monotonicMs: anker.monotonicMs,
      bootId: anker.bootId,
      wallUtcMs: anker.wallUtcMs,
      framerate: rate,
      dropFrame: anker.dropFrame,
      source: "manual",
    },
    { monotonicMs: p.monotonicMs, bootId: p.bootId, wallUtcMs: p.wallUtcMs },
    spec,
  );
  return formatTimecode(t.frames, spec);
}

interface Take {
  readonly start: synk.Synkpost;
  readonly slutt: synk.Synkpost | null;
}

/**
 * Kortere enn dette, uten én eneste hake, er et FEILTRYKK.
 *
 * «Start synk» og «Ferdig» rett etter hverandre er noen som kom borti
 * knappen, og da skal det ikke ligge igjen en tidskode klipperen leter
 * etter forgjeves. Synken trekkes tilbake uten å spørre.
 *
 * MEN IKKE EN LANG SYNK UTEN HAKER. Mange reportere gjør synken som en
 * samtale og huker av etterpå — da er tjue minutter uten en eneste hake
 * helt riktig, og å slette den ville tatt tidskoden de skal fylle ut mot.
 * Der spør flata i stedet. De to tilfellene skilles på varighet, fordi det
 * er den eneste forskjellen som faktisk finnes mellom dem.
 */
const FEILTRYKK_MS = 20000;

export function SynkScreen({ oekt, iDag = true }: {
  readonly oekt: Oekt;
  /**
   * Staar vi paa dagen som gaar?
   *
   * ========  MAN SYNKER IKKE PAA EN ANNEN DAG  ========
   *
   * Brukeren 03.11.2026: «Gjoer at man er noedt til aa vaere i den aktive
   * dagen for aa faa lov til aa logge og synke en deltaker. De gaar ut ifra
   * hendelser som skjer naa, saa det vil aldri vaere aktuelt aa logge noe
   * paa en kommende dag.»
   *
   * Et synkspoersmaal som hukes av setter en MARKOER i loggen, med klokka
   * naa og dagens anker. Lagt i gaarsdagens oekt er det en tidskode ingen
   * kan stole paa — samme grunn som i liveloggen.
   *
   * Det man fortsatt kan paa en tidligere dag: lese, og skrive SVARET paa
   * et spoersmaal. Svaret er tekst og ingen post, og det skrives som regel
   * i ettertid — det er hele grunnen til at feltet finnes.
   */
  readonly iDag?: boolean;
}): JSX.Element {
  const [data, setData] = useState<synk.Synkdata>(synk.TOM);
  const [liste, setListe] = useState<su.SuData>(su.TOM);
  const [klar, setKlar] = useState(false);
  const [feil, setFeil] = useState("");
  /** Deltakeren man sitter med. Null betyr at velgeren står framme. */
  const [valgt, setValgt] = useState<string | null>(null);
  /**
   * Hvilket spørsmål som står framme før synken avsluttes.
   *
   * `null` er ingen. `"haker"` er det vanlige: noen spørsmål står uten hake.
   * `"tom"` er synken der INGENTING er huket av, og den er tvetydig — se
   * `FEILTRYKK_MS`.
   */
  const [bekreft, setBekreft] = useState<null | "haker" | "tom">(null);
  /** Svartekst under redigering, etter svar-id. Tømmes når den er lagret. */
  const [utkast, setUtkast] = useState<Record<string, string>>({});
  /** Et nytt spørsmål under skriving. Reporteren legger til underveis. */
  const [nytt, setNytt] = useState("");
  /**
   * Svarfeltet markøren skal hoppe til.
   *
   * HUKER DU AV, SKAL DU KUNNE SKRIVE. Å måtte flytte hånda til musa og
   * treffe en linje er feil arbeid midt i en samtale — du huker av med én
   * hand og skriver videre med den andre. Bestilt 25.10.2026.
   */
  const [fokus, setFokus] = useState<string | null>(null);
  /**
   * Slettemodus, slått på med et langt trykk — som i appen.
   *
   * SAMME BEVEGELSE, IKKE BARE SAMME IDÉ. Et kryss som alltid står der er
   * for lett å komme borti, og loggen er det vi aldri har råd til å miste.
   * I appen holder man inne på en post for å få fram papirkurven, og så
   * krysser man ut. Her er det likt: samme 500 ms, samme åtte piksler
   * bevegelse som avbryter, og samme kurv som glir ut fra venstre.
   * Omgjort 26.10.2026.
   */
  const [slettemodus, setSlettemodus] = useState(false);
  /** Trykket som er i gang. Ett om gangen — to fingre ga to tidsur. */
  const trykk = useRef<{
    id: number; x: number; y: number; timer: number; move: (e: PointerEvent) => void;
  } | null>(null);
  /** Hindrer at Enter og blur sender det samme spørsmålet to ganger. */
  const sender = useRef(false);
  /**
   * Svaret lagres MENS DU SKRIVER, ikke bare når feltet mister fokus.
   *
   * Blur alene er for skjørt for det som er hele poenget med flata: lukker
   * du lokket, bytter fane eller går fra maskinen midt i en setning, er
   * svaret borte uten at noe sa fra. Pausen er lang nok til at en setning
   * blir én skriving og ikke tjue.
   */
  const skrivetimer = useRef<Record<string, number>>({});

  const spec = useMemo(() => specFor(oekt), [oekt]);
  const hvem = useMemo((): synk.Hvem => ({
    produksjonId: oekt.production.id,
    opptaksdag: oekt.shootDay,
    medlemId: oekt.member.id,
    framerate: oekt.production.framerate,
    dropFrame: oekt.production.dropFrame,
    /* Er man paa et team, skriver synken i TEAMETS oekt — den samme
       liveloggen bruker. Ellers kan de to ikke bli én rapport. */
    teamId: oekt.member.unitId ?? null,
  }), [oekt]);

  /* Returnerer det den hentet: den som nettopp endret noe maa kunne bygge
     loggposten av FERSKE data, ikke av `data` som ennaa ikke er satt. */
  const last = useCallback(async (): Promise<synk.Synkdata> => {
    const [d, l] = await Promise.all([synk.hent(hvem, spec), su.hent(hvem.produksjonId)]);
    setData(d);
    setListe(l);
    setKlar(true);
    return d;
  }, [hvem, spec]);

  useEffect(() => { void last(); }, [last]);

  /*
   * Markøren settes ETTER at feltet er tegnet.
   *
   * Feltet finnes ikke før avhukingen er lest tilbake, så dette kan ikke
   * gjøres i klikket. `preventScroll` er med fordi flata ellers hopper på
   * en liste som er lengre enn vinduet.
   */
  useEffect(() => {
    if (fokus === null) return;
    const el = document.querySelector<HTMLTextAreaElement>(`[data-svar="${fokus}"]`);
    if (el !== null) {
      el.focus({ preventScroll: true });
      el.setSelectionRange(el.value.length, el.value.length);
    }
    setFokus(null);
  }, [fokus, data.svar]);

  /*
   * HAKEN MÅ GÅ BEGGE VEIER, LIVE.
   *
   * Regi har samme lista i appen. Huker den ene av, skal den andre se det —
   * ellers stilles spørsmålet to ganger, og det er feilen lista finnes for å
   * hindre. `su_answer` ligger i Realtime fra 20261024090000.
   */
  useLevende("synk", ["su_answer", "su_question", "log_entry", "participant"], last);

  /*
   * Rammene krever ANKERET, ikke oekta.
   *
   * Oekta lages av den foerste markoeren — aa aapne fanen skal ikke aapne en
   * logg. Se `aapneOekt` i state/synk.ts.
   */
  const rammer = useMemo((): synk.Rammer | null => {
    if (data.anker === null) return null;
    return {
      oektId: data.oektId,
      produksjonId: hvem.produksjonId,
      opptaksdag: hvem.opptaksdag,
      ankerId: data.anker.id,
      ankerBootId: data.anker.bootId,
      medlemId: hvem.medlemId,
      teamId: hvem.teamId ?? null,
    };
  }, [data.anker, data.oektId, hvem]);

  /**
   * Synkene per deltaker, med sluttposten paret på.
   *
   * Paret på DELTAKER OG TID: slutten er den første SYNK SLUTT for samme
   * person etter starten. To reportere som synker hver sin person samtidig
   * skal ikke kunne stjele hverandres slutt.
   */
  const takes = useMemo((): Map<string, readonly Take[]> => {
    const ut = new Map<string, Take[]>();
    const sluttposter = data.poster
      .filter((p) => p.label === synk.SLUTT)
      .sort((a, b) => a.wallUtcMs - b.wallUtcMs);
    const brukt = new Set<string>();

    for (const start of data.poster
      .filter((p) => p.label === synk.START)
      .sort((a, b) => a.wallUtcMs - b.wallUtcMs)) {
      const d = start.deltakerId;
      if (d === null) continue;
      const slutt = sluttposter.find((s) =>
        s.deltakerId === d && s.wallUtcMs >= start.wallUtcMs && !brukt.has(s.id)) ?? null;
      if (slutt !== null) brukt.add(slutt.id);
      const rad = ut.get(d) ?? [];
      rad.push({ start, slutt });
      ut.set(d, rad);
    }
    return ut;
  }, [data.poster]);

  /** De generelle spørsmålene. Stilles til alle, og står øverst — som i appen. */
  const generelle = useMemo(
    () => liste.spoersmaal.filter((q) => q.deltakerId === null),
    [liste.spoersmaal],
  );

  const egneFor = useCallback((deltakerId: string): readonly su.Spoersmaal[] =>
    liste.spoersmaal.filter((q) => q.deltakerId === deltakerId), [liste.spoersmaal]);

  const svarFor = useCallback((postId: string, spoersmaalId: string): synk.Svar | null =>
    data.svar.find((v) => v.postId === postId && v.spoersmaalId === spoersmaalId) ?? null,
  [data.svar]);

  /**
   * Spørsmål som er stilt til denne personen — AV HVEM SOM HELST.
   *
   * ET SPØRSMÅL SOM ER STILT ER STILT FOR ALLE. Avgjort 25.10.2026. Har regi
   * spurt ALI om noe fra appen, skal reporteren se det, ellers får deltakeren
   * samme spørsmål to ganger. Det er PARET (spørsmål, deltaker) som er stilt,
   * ikke spørsmålet: «Hvordan har du det?» til ALI er ikke det samme som til
   * BEN.
   *
   * `hent` har alt filtrert bort svar der posten er slettet, så invarianten
   * holder: stilt betyr at svaret finnes og posten lever.
   */
  const stiltFor = useCallback((deltakerId: string): ReadonlySet<string> =>
    new Set(data.svar
      .filter((v) => v.deltakerId === deltakerId)
      .map((v) => v.spoersmaalId)),
  [data.svar]);

  /** Mine egne synkposter. Bare de kan jeg huke av og angre i. */
  const egnePoster = useMemo(
    () => new Set(data.poster.filter((p) => p.label === synk.START).map((p) => p.id)),
    [data.poster],
  );

  const start = useCallback(async (d: su.Deltaker): Promise<void> => {
    if (rammer === null) return;
    setFeil("");
    /* Tidskoden fanges VED TRYKKET, ikke ved lagringen. */
    const post = await synk.startSynk(rammer, d.id, d.forkortelse, synk.naa());
    if (post === null) { setFeil("Fikk ikke satt starten i loggen. Prøv igjen."); return; }
    await last();
  }, [rammer, last]);

  const avslutt = useCallback(async (d: su.Deltaker): Promise<void> => {
    if (rammer === null) return;
    setFeil("");
    setBekreft(null);
    const post = await synk.avsluttSynk(rammer, d.id, d.forkortelse, synk.naa());
    if (post === null) { setFeil("Fikk ikke satt slutten i loggen. Prøv igjen."); return; }
    await last();
  }, [rammer, last]);

  /**
   * Trekker synken tilbake. Ingen slutt-markør, og starten tombstones.
   *
   * Et feiltrykk skal ikke legge igjen noe i loggen — hverken en start uten
   * innhold eller en slutt som gjør den til et intervall.
   */
  const trekkTilbake = useCallback(async (
    startId: string, sluttId?: string,
  ): Promise<void> => {
    setFeil("");
    setBekreft(null);
    setSlettemodus(false);
    if (!await synk.trekkTilbake(startId)) {
      setFeil("Fikk ikke trukket synken tilbake. Prøv igjen.");
      return;
    }
    /* Slutten hoerer til starten. Blir den staaende alene, er den en
       markoer uten noe foran seg. */
    if (sluttId !== undefined) await synk.trekkTilbake(sluttId);
    await last();
  }, [last]);

  /**
   * Skriver synken inn i loggposten, slik redaktøren ser den.
   *
   * Kalles etter HVER endring — hake, angret hake, nytt svar — fordi posten
   * er det eneste redaktørens flate leser. Tar listene som argument framfor
   * å lese `data`, som ennå ikke er oppdatert når dette kalles.
   */
  const byggPost = useCallback(async (
    postId: string, svar: readonly synk.Svar[], spm: readonly su.Spoersmaal[],
    forkortelse: string,
  ): Promise<void> => {
    const linjer = svar
      .filter((v) => v.postId === postId)
      .map((v) => ({
        spoersmaal: spm.find((q) => q.id === v.spoersmaalId)?.tekst ?? "",
        svar: v.svar,
      }))
      .filter((l) => l.spoersmaal !== "");
    await synk.skrivInnIPosten(postId, forkortelse, linjer);
  }, []);

  const veksleHake = useCallback(async (
    postId: string, q: su.Spoersmaal, deltakerId: string, forkortelse: string,
  ): Promise<void> => {
    setFeil("");
    const har = svarFor(postId, q.id);
    if (har !== null) {
      if (!await synk.angreHuk(har.id)) {
        setFeil("Fikk ikke fjernet haken. Prøv igjen.");
        return;
      }
      const d = await last();
      await byggPost(postId, d.svar, liste.spoersmaal, forkortelse);
      return;
    }
    const ny = await synk.hukAv(hvem.produksjonId, postId, q.id, deltakerId);
    if (ny === null) { setFeil("Fikk ikke lagret haken. Prøv igjen."); return; }
    const d = await last();
    await byggPost(postId, d.svar, liste.spoersmaal, forkortelse);
    /* Og markøren rett i svarfeltet til det du nettopp huket av. */
    setFokus(ny.id);
  }, [svarFor, hvem.produksjonId, last, byggPost, liste.spoersmaal]);

  const lagreSvar = useCallback(async (
    svarId: string, postId: string, forkortelse: string, naa?: string,
  ): Promise<void> => {
    /* `naa` kommer fra skrivingen selv. Uten den leser vi utkastet, og det
       er riktig ved blur — men en tidsutloeser kan ha en eldre lukking. */
    const tekst = naa ?? utkast[svarId];
    if (tekst === undefined) return;
    setFeil("");
    const ok = await synk.skrivSvar(svarId, tekst, hvem.medlemId);
    if (!ok) { setFeil("Fikk ikke lagret svaret. Prøv igjen."); return; }

    const d = await last();
    /* Svaret skal staa i posten, ikke bare i tabellen. */
    await byggPost(postId, d.svar, liste.spoersmaal, forkortelse);

    /*
     * ========  UTKASTET SLIPPES FOERST NAA  ========
     *
     * Her sto slettingen FOER `last()`, og da falt feltet tilbake paa
     * `svar.svar` — verdien fra forrige innlesing, altsaa den gamle.
     * Teksten forsvant et oeyeblikk og kom tilbake naar svaret var
     * hentet. Brukeren 02.11.2026 saa nettopp det.
     *
     * Og bare hvis utkastet fortsatt ER det vi lagret: rakk noen aa
     * skrive en bokstav til mens vi ventet paa serveren, er den
     * bokstaven ferskere enn svaret vi nettopp sendte.
     */
    setUtkast((p) => {
      if (p[svarId] !== tekst) return p;
      const n = { ...p };
      delete n[svarId];
      return n;
    });
  }, [utkast, hvem.medlemId, last, byggPost, liste.spoersmaal]);

  /**
   * Nytt spørsmål, skrevet der og da.
   *
   * REPORTEREN TRENGER DETTE MER ENN REDAKTØREN. Noe dukker opp i samtalen,
   * og det skal kunne stilles før man glemmer det — regi har hatt samme
   * knapp i appen hele tiden. Bestilt 25.10.2026.
   *
   * SPØRSMÅLET HØRER TIL PRODUKSJONEN, ikke til synken. Det er samme tabell
   * redaktøren skriver i, så det dukker opp på Spørsmål-flata og i appen med
   * én gang — og det er riktig: er spørsmålet verdt å stille, er det verdt at
   * de andre ser at det ble stilt.
   */
  const leggTil = useCallback(async (deltakerId: string): Promise<void> => {
    if (sender.current) return;
    const tekst = nytt.trim();
    if (tekst.length === 0) return;
    sender.current = true;
    setNytt("");
    const antall = liste.spoersmaal.filter((q) => q.deltakerId === deltakerId).length;
    const q = await su.leggTil(
      hvem.produksjonId, deltakerId, tekst, antall, hvem.opptaksdag,
    );
    sender.current = false;
    if (q === null) {
      /* Teksten kommer tilbake. Ingenting skal gå tapt fordi nettet svikter. */
      setNytt(tekst);
      setFeil("Fikk ikke lagret spørsmålet. Prøv igjen.");
      return;
    }
    setFeil("");
    await last();
  }, [nytt, liste.spoersmaal, hvem.produksjonId, hvem.opptaksdag, last]);

  /*
   * Trykket avbrytes av bevegelse, og leses fra DOKUMENTET.
   *
   * Flyttes fingeren mer enn åtte piksler, er det en rulling og ikke et
   * hold. Lytteren ligger på dokumentet fordi en rullende boks kan ta
   * pekeren med `setPointerCapture` — da ser posten aldri bevegelsen, og
   * slettemodus ville slått inn midt i en rulling. Samme løsning som i
   * appen.
   */
  const avbrytTrykk = useCallback((): void => {
    const t = trykk.current;
    if (t === null) return;
    window.clearTimeout(t.timer);
    document.removeEventListener("pointermove", t.move);
    trykk.current = null;
  }, []);

  const startTrykk = useCallback((e: React.PointerEvent): void => {
    if (slettemodus || trykk.current !== null) return;
    const x = e.clientX;
    const y = e.clientY;
    const move = (ev: PointerEvent): void => {
      if (ev.pointerId !== e.pointerId) return;
      if (Math.abs(ev.clientY - y) > 8 || Math.abs(ev.clientX - x) > 8) avbrytTrykk();
    };
    document.addEventListener("pointermove", move);
    trykk.current = {
      id: e.pointerId,
      x,
      y,
      move,
      timer: window.setTimeout(() => {
        if (trykk.current === null) return;
        avbrytTrykk();
        setSlettemodus(true);
      }, 500),
    };
  }, [slettemodus, avbrytTrykk]);

  /* Et trykk skal ikke overleve flata. */
  useEffect(() => () => { avbrytTrykk(); }, [avbrytTrykk]);

  /*
   * VEIEN UT ER AA TRYKKE ET ANNET STED, som i appen. Escape ogsaa, fordi
   * dette er en PC og den tasten betyr nettopp dette her.
   */
  useEffect(() => {
    if (!slettemodus) return;
    const av = (e: Event): void => {
      if (e.target instanceof Element && e.target.closest(".synk__papirkurv") !== null) return;
      setSlettemodus(false);
    };
    const tast = (e: KeyboardEvent): void => { if (e.key === "Escape") setSlettemodus(false); };
    document.addEventListener("pointerdown", av);
    document.addEventListener("keydown", tast);
    return () => {
      document.removeEventListener("pointerdown", av);
      document.removeEventListener("keydown", tast);
    };
  }, [slettemodus]);

  if (!klar) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">Henter synkene …</p>
      </div></div>
    );
  }

  if (data.anker === null || rammer === null) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">
          Fikk ikke åpnet loggen for dagen. Uten den finnes det ingen tidskode å
          sette synken på.
        </p>
      </div></div>
    );
  }

  const anker = data.anker;
  const person = liste.deltakere.find((d) => d.id === valgt) ?? null;

  /* ---- Velgeren -------------------------------------------------------
   *
   * RUTENETTET HØRER HIT, OG BARE HIT. Før man har valgt er spørsmålet hvem
   * som mangler hva, og det leses i ett blikk. Etterpå sitter man med én
   * person, og da er de andre i veien.
   */
  if (person === null) {
    return (
      <div className="flate">
        <div className="flate__innhold synk">
          {feil === "" ? null : <p className="synk__melding synk__melding--feil">{feil}</p>}
          <p className="synk__melding">
            {liste.deltakere.length === 0
              ? "Ingen deltakere er satt opp ennå. Redaktøren legger dem inn på "
                + "Spørsmål-flata."
              : "Velg den du skal synke."}
          </p>

          <div className="synk__bord">
            {liste.deltakere.map((d) => {
              const mine = [...generelle, ...egneFor(d.id)];
              const stilt = stiltFor(d.id);
              const gjort = mine.filter((q) => stilt.has(q.id)).length;
              const gaar = (takes.get(d.id) ?? []).some((t) => t.slutt === null);

              return (
                <button
                  type="button"
                  className={`synk__velg${gaar ? " synk__velg--gaar" : ""}`}
                  key={d.id}
                  onClick={() => { setValgt(d.id); setBekreft(null); }}
                >
                  <span className="synk__velg-merke">{d.forkortelse}</span>
                  <span className="synk__velg-navn">{d.navn}</span>
                  <span className="synk__velg-tall">
                    {gaar ? "synken går" : `${gjort} av ${mine.length} stilt`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ---- Synken ---------------------------------------------------------- */

  const stilt = stiltFor(person.id);
  const egne = egneFor(person.id);
  const mine = [...generelle, ...egne];

  /*
   * PICKUPS: det som har staatt for lenge.
   *
   * Et spørsmål som ikke er stilt et døgn etter at det ble skrevet angår
   * som regel en tidligere historie som er fulgt opp, og det er noe annet
   * enn det som er aktuelt nå. Regelen ligger i packages/shared/synk, slik
   * at appen og nettflata aldri kan bli uenige om hva en pickup er.
   *
   * Tiden leses ÉN gang per tegning. Leste hver linje sin egen `Date.now()`,
   * kunne to spørsmål skrevet i samme sekund havnet i hver sin gruppe.
   */
  const naa = Date.now();
  const pickup = (q: su.Spoersmaal): boolean =>
    erPickup(
      { skrevetMs: q.skrevetMs, generelt: q.deltakerId === null },
      stilt.has(q.id),
      naa,
    );
  /* De generelle staar urørt: de stilles hver gang man synker, og hører til
     lista for godt. Derfor er det bare personens EGNE som kan bli pickups. */
  const egneNaa = egne.filter((q) => !pickup(q));
  const pickups = egne.filter(pickup);
  const rad = takes.get(person.id) ?? [];
  const aktiv = rad.find((t) => t.slutt === null) ?? null;
  /* Haken hører til DEN AKTIVE synken når en går. Gjør den det ikke, hører
     den til den siste — da fyller man ut lista i ettertid, som er vanlig. */
  const take = aktiv ?? rad[rad.length - 1] ?? null;
  const igjen = mine.length - mine.filter((q) => stilt.has(q.id)).length;

  /**
   * Én linje i LISTA til venstre: hake og spørsmål, ingenting mer.
   *
   * Svaret er flyttet til synk-loggen til høyre. Lista er en sjekkliste man
   * leser nedover mens man snakker, og et tekstfelt mellom hver linje gjorde
   * den til et skjema man ikke kan skumme.
   */
  const linje = (q: su.Spoersmaal): JSX.Element => {
    const mitt = take === null ? null : svarFor(take.start.id, q.id);
    /* Stilt av noen andre: haken staar, men den er ikke min aa angre. */
    const andres = mitt === null && stilt.has(q.id);
    const haket = mitt !== null || andres;

    return (
      <li className="synk__linje" key={q.id}>
        <button
          type="button"
          className={`synk__hake${haket ? " synk__hake--paa" : ""}`}
          disabled={take === null || andres}
          aria-pressed={haket}
          title={andres ? "Stilt i en annen logg" : undefined}
          aria-label={andres
            ? `Stilt i en annen logg: ${q.tekst}`
            : mitt === null ? `Huk av: ${q.tekst}` : `Fjern haken: ${q.tekst}`}
          onClick={() => {
            if (take === null || andres) return;
            void veksleHake(take.start.id, q, person.id, person.forkortelse);
          }}
        >
          {haket ? "✓" : ""}
        </button>
        {/*
          * OPPTAKSDAGEN SPØRSMÅLET ER FRA.
          *
          * Regi bruker den til å hjelpe deltakeren å huske: «dette er fra
          * dag 10, da dere var i hagen». Bestilt 26.10.2026.
          *
          * BARE PÅ DE PERSONLIGE. Et generelt spørsmål er evergreen — det
          * stilles hver gang man synker — og «fra dag 0» om «Hvordan har du
          * det?» er ikke en hjelp, det er støy på hver eneste linje.
          */}
        {q.deltakerId === null || q.opptaksdag === null ? null : (
          <span className="synk__dag mono">D{q.opptaksdag}</span>
        )}
        <span className={`synk__spm${haket ? " synk__spm--stilt" : ""}`}>{q.tekst}</span>
      </li>
    );
  };

  /**
   * SYNK-LOGGEN til høyre, bygd av de SAMME delene som Logg-flata.
   *
   * `.loggkort`, `.bolk`, `.post`, `.post__stempel` — ikke egne klasser.
   * Det er den samme posten redaktøren leser, og jo mer flatene deler, jo
   * mindre er det å lære. En flate som ser ny ut lærer brukeren at den ER
   * ny, og det er den ikke. Omgjort 25.10.2026.
   *
   * SVARET SKRIVES HER, i posten, og markøren hopper hit i det du huker av
   * — se `fokus`. Feltene står så lenge dagen gjør det, så lista kan fylles
   * ut i ettertid.
   */
  const boks = (t: Take): JSX.Element => {
    /* Rekkefølgen svarene ble huket av i. Det ER loggen. */
    const svarene = data.svar
      .filter((v) => v.postId === t.start.id)
      .map((v) => ({ svar: v, q: mine.find((x) => x.id === v.spoersmaalId) ?? null }));

    return (
      <div className="bolk" key={t.start.id}>
        {/*
          * EGEN POST, EGEN HAND. Dette er reporterens egen logg, og da skal
          * hen kunne trekke tilbake det hen selv har skrevet — samme regel
          * som i appen. Ingen rører noe de ikke har skrevet.
          *
          * Kurven glir ut fra venstre i slettemodus, akkurat som på
          * Logg-skjermen i appen: posten blir smalere og gir plass, den
          * skyves ikke til side.
          */}
        <div className={`synk__rad${slettemodus ? " synk__rad--sletter" : ""}`}>
          <button
            type="button"
            className="synk__papirkurv"
            onClick={() => { void trekkTilbake(t.start.id, t.slutt?.id); }}
            aria-label={`Fjern synken ${tidskode(anker, t.start)}`}
            tabIndex={slettemodus ? 0 : -1}
          >
            ⌫
          </button>
        <article
          className="post"
          onPointerDown={startTrykk}
          onPointerUp={() => { avbrytTrykk(); }}
          onPointerCancel={() => { avbrytTrykk(); }}
        >
          {/* Stripa er SYNK-markørens farge, den samme appen setter. */}
          <span className="post__stripe" style={{ background: "var(--tc)" }} />
          <div className="post__hode">
            <span className="mono post__stempel">{tidskode(anker, t.start)}</span>
            <span className="mono post__etikett">{synk.START}</span>
            <span className="synk__posttid mono">
              {t.slutt === null ? "går" : tidskode(anker, t.slutt)}
            </span>
          </div>

          {svarene.length === 0 ? (
            <p className="mono post__tekst">{person.forkortelse}</p>
          ) : (
            <div className="synk__poster">
              <p className="mono post__tekst">{person.forkortelse}</p>
              {svarene.map(({ svar, q }) => (
                <div className="synk__par" key={svar.id}>
                  <p className="mono post__tekst">{q === null ? "" : q.tekst}</p>
                  <textarea
                    className="synk__svar"
                    data-svar={svar.id}
                    value={utkast[svar.id] ?? svar.svar}
                    rows={2}
                    placeholder="Hva svarte de?"
                    aria-label={`Svar på «${q === null ? "" : q.tekst}»`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setUtkast((p) => ({ ...p, [svar.id]: v }));
                      /* Lagres av seg selv naar du tar en pause. */
                      window.clearTimeout(skrivetimer.current[svar.id]);
                      skrivetimer.current[svar.id] = window.setTimeout(() => {
                        void lagreSvar(svar.id, t.start.id, person.forkortelse, v);
                      }, 900);
                    }}
                    onBlur={() => {
                      window.clearTimeout(skrivetimer.current[svar.id]);
                      void lagreSvar(svar.id, t.start.id, person.forkortelse);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </article>
        </div>
      </div>
    );
  };

  return (
    <div className="flate">
      <div className="flate__innhold synk">
        {feil === "" ? null : <p className="synk__melding synk__melding--feil">{feil}</p>}

        {/*
          * HVEM MAN SITTER MED, øverst — som i appen. «Bytt» er veien ut
          * uten å avslutte: velger man feil person, skal man ikke måtte
          * late som synken er gjort for å komme seg vekk.
          *
          * Hodet staar over BEGGE spaltene: det gjelder synken, ikke lista.
          */}
        <div className="synk__flate">
          <header className="synk__hvem">
            <span className="synk__hvem-merke">{person.forkortelse}</span>
            <span className="synk__hvem-navn">{person.navn}</span>
            <button
              type="button"
              className="synk__knapp"
              onClick={() => { setValgt(null); setBekreft(null); }}
            >
              Bytt
            </button>
          </header>

          {!iDag ? (
            <p className="synk__melding">
              Du leser opptaksdag {oekt.shootDay}. En synk settes med klokka
              nå, så den hører til dagen som går — trykk datoen i toppen for
              å komme tilbake. Svarene kan du skrive her uansett.
            </p>
          ) : aktiv === null ? (
            <button
              type="button"
              className="synk__knapp synk__knapp--hoved synk__knapp--bred"
              onClick={() => { void start(person); }}
            >
              Start synk
            </button>
          ) : (
            <div className="synk__gaar">
              <span className="synk__gaar-tid mono">{tidskode(anker, aktiv.start)}</span>
              <span className="synk__gaar-ord">synken går</span>
              {bekreft !== null ? null : (
                <button
                  type="button"
                  className="synk__knapp"
                  onClick={() => {
                    /*
                      * TRE VEIER UT, og flata velger selv hvilken.
                      *
                      * Ingen haker og under tjue sekunder: feiltrykk, og den
                      * trekkes tilbake uten å spørre. Ingen haker, men lang:
                      * trolig en samtale som skal hukes av etterpå, og da
                      * spør vi. Ellers det vanlige spørsmålet.
                      */
                    const haker = data.svar
                      .filter((v) => v.postId === aktiv.start.id).length;
                    const varighet = Date.now() - aktiv.start.wallUtcMs;
                    if (haker === 0 && varighet < FEILTRYKK_MS) {
                      void trekkTilbake(aktiv.start.id);
                      return;
                    }
                    setBekreft(haker === 0 ? "tom" : "haker");
                  }}
                >
                  Ferdig med synk
                </button>
              )}
            </div>
          )}

          {/*
            * SPØRSMÅLET STILLES I FLATA, ikke i en nettleserdialog. Den
            * dialogen stopper hele siden og ser ut som den kommer fra noe
            * annet enn det du står i. Samme løsning som i shotlista.
            *
            * Og det er et SPØRSMÅL, ikke en sperre: har man snakket om noe
            * uten å huke av, er «ja, jeg er ferdig» likevel riktig svar.
            */}
          {bekreft !== null && aktiv !== null ? (
            <div className="synk__bekreft">
              <p className="synk__bekreft-tekst">
                {bekreft === "tom"
                  ? "Ingen spørsmål er huket av. Var dette et feiltrykk, eller "
                    + "skal du huke av etterpå?"
                  : igjen === 0
                    ? "Alle spørsmålene er huket av. Avslutte synken?"
                    : `${igjen} av ${mine.length} spørsmål står uten hake. `
                      + "Husket du å huke av alt du stilte?"}
              </p>
              <div className="synk__bekreft-valg">
                {bekreft === "tom" ? (
                  <>
                    <button
                      type="button"
                      className="synk__knapp synk__knapp--hoved"
                      onClick={() => { void trekkTilbake(aktiv.start.id); }}
                    >
                      Feiltrykk, fjern den
                    </button>
                    <button
                      type="button"
                      className="synk__knapp"
                      onClick={() => { void avslutt(person); }}
                    >
                      Behold, jeg huker av etterpå
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="synk__knapp synk__knapp--hoved"
                      onClick={() => { void avslutt(person); }}
                    >
                      Ferdig
                    </button>
                    <button
                      type="button"
                      className="synk__knapp"
                      onClick={() => { setBekreft(null); }}
                    >
                      Tilbake til lista
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/*
            * TO SPALTER: sjekklista til venstre, synk-loggen til høyre.
            *
            * Lista leses nedover mens man snakker; loggen er det som havner i
            * materialet og hos redaktøren. De gjør to ulike ting, og da skal
            * de ikke stå i samme kolonne. Bestilt 25.10.2026.
            */}
          <div className="synk__to">
          {/*
            * BEGGE SPALTENE ER BOKSER, og hver boks blar for seg.
            *
            * Foer laa lista loest i flata mens loggen hadde sin egen
            * rulling: da blaadde man i to ting samtidig, og hjulet gjorde
            * ulike ting etter hvor pekeren sto. Én ramme per jobb, og flata
            * selv staar stille. Omgjort 25.10.2026.
            */}
          <section className="loggkort" aria-label={`Spørsmål til ${person.navn}`}>
            <header className="loggkort__hode">
              <span className="loggkort__merke mono">SPØRSMÅL</span>
              <span className="loggkort__navn">{person.navn}</span>
              <span className="loggkort__alder mono">
                {mine.length - igjen} av {mine.length}
              </span>
            </header>
            <div className="samtale synk__spalte">
          {mine.length === 0 ? (
            <p className="synk__tomt">
              Ingen spørsmål til {person.forkortelse} ennå. Redaktøren legger dem
              inn på Spørsmål-flata.
            </p>
          ) : null}

          {/*
            * HAKEN TRENGER EN POST AA HENGE PAA.
            *
            * Foer synken er startet finnes ingen loggpost, og da er haken
            * avslaatt. En avslaatt knapp som ikke sier hvorfor er en blindvei:
            * man trykker, ingenting skjer, og det staar ingenting noe sted.
            */}
          {take === null && mine.length > 0 ? (
            <p className="synk__tomt">Start synken for å huke av.</p>
          ) : null}

          {/* Gruppene som i appen: det som gjelder alle, så personens egne. */}
          {generelle.length === 0 ? null : (
            <>
              <span className="synk__gruppe">Til alle</span>
              <ul className="synk__liste">{generelle.map(linje)}</ul>
            </>
          )}
          {/*
            * PERSONENS EGEN GRUPPE STAAR ALLTID, ogsaa tom.
            *
            * Feltet for et nytt spoersmaal ligger i den, og da maa gruppa
            * finnes foer det foerste spoersmaalet gjoer det — ellers er det
            * ingen plass aa skrive det inn paa.
            */}
          <span className="synk__gruppe">{person.navn}</span>
          {egneNaa.length === 0 ? null : (
            <ul className="synk__liste">{egneNaa.map(linje)}</ul>
          )}
          <input
            className="synk__nytt"
            value={nytt}
            onChange={(e) => { setNytt(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter") void leggTil(person.id); }}
            onBlur={() => { void leggTil(person.id); }}
            placeholder={egne.length === 0
              ? `Første spørsmål til ${person.forkortelse}`
              : "Nytt spørsmål"}
            aria-label={`Nytt spørsmål til ${person.navn}`}
          />

          {/*
            * PICKUPS NEDERST, og med sin egen tittel.
            *
            * De er ikke borte — de skal fortsatt kunne stilles, og hakene
            * virker som ellers. De står bare for seg, slik at det som er
            * aktuelt i dag leses først.
            */}
          {pickups.length === 0 ? null : (
            <>
              <span className="synk__gruppe synk__gruppe--pickup">
                Pickups
                <span className="synk__gruppe-hint">
                  skrevet for over et døgn siden
                </span>
              </span>
              <ul className="synk__liste">{pickups.map(linje)}</ul>
            </>
          )}
            </div>
          </section>

          {/* Synk-loggen. Nyeste synk nederst, som i en logg. */}
          <section className="loggkort" aria-label={`Synk-logg for ${person.navn}`}>
            <header className="loggkort__hode">
              <span className="loggkort__merke mono">{person.forkortelse}</span>
              <span className="loggkort__navn">{person.navn}</span>
              <span className="loggkort__alder mono">
                {aktiv !== null ? "synken går" : rad.length === 0 ? "ingen synk" : "lukket"}
              </span>
            </header>
            <div className="samtale">
              {rad.length === 0 ? (
                <p className="samtale__tom">
                  Synk-loggen kommer her når du starter synken. Tidskoden er det
                  klipperen leter etter.
                </p>
              ) : rad.map(boks)}
            </div>
          </section>
          </div>

        </div>
      </div>
    </div>
  );
}
